import { type FC } from "react";

import { Tooltip } from "@snapchat/snap-design-system";

import { type ExclusiveLensStatus, type LensCreatorPayoutEligibility } from "../../../../../gql-generated/types";
import { LensRowBadge } from "../LensRowBadge";
import { badgeWarning } from "../LensRowBadge.styles";
import {
    BadgeContentLensCreatorPayoutNote,
    BadgeContentLensCreatorPayoutTOS,
    BadgeContentLensCreatorPayoutTOSCTA,
    BadgeTitleLensCreatorPayoutTOSActionRequired,
} from "../messages.generated";

import { getBadgeConfig, isEligibleForAnyProgram } from "./utils";

interface LensCreatorPayoutBadgeProps {
    lensCreatorPayoutEligibility: LensCreatorPayoutEligibility;
    acceptedLatestTosVersion: boolean;
    exclusiveLensStatus: ExclusiveLensStatus;
}

const TOS_URL = "https://snap.com/en-US/terms/lens-creator-rewards-terms";

const renderNoteElement = () => (
    <em>
        <BadgeContentLensCreatorPayoutNote />
    </em>
);

export const LensCreatorPayoutBadge: FC<LensCreatorPayoutBadgeProps> = ({
    lensCreatorPayoutEligibility,
    acceptedLatestTosVersion,
    exclusiveLensStatus,
}) => {
    // Handle TOS not accepted case
    if (!acceptedLatestTosVersion && isEligibleForAnyProgram(exclusiveLensStatus, lensCreatorPayoutEligibility)) {
        return (
            <Tooltip
                title={<BadgeTitleLensCreatorPayoutTOSActionRequired />}
                content={
                    <BadgeContentLensCreatorPayoutTOS
                        note={renderNoteElement()}
                        tosLink={
                            <a
                                href={TOS_URL}
                                target="_blank"
                                rel="noreferrer"
                                onClick={(e) => {
                                    e.stopPropagation();
                                }}
                            >
                                <BadgeContentLensCreatorPayoutTOSCTA />
                            </a>
                        }
                    />
                }
                textAlign="center"
            >
                <LensRowBadge className={badgeWarning}>!</LensRowBadge>
            </Tooltip>
        );
    }

    // Handle accepted TOS case
    if (!acceptedLatestTosVersion) {
        return null;
    }

    const config = getBadgeConfig(exclusiveLensStatus, lensCreatorPayoutEligibility);
    if (!config) {
        return null;
    }

    // Render content with note if needed
    const renderContentWithNote = () => {
        if (!config.includeNote) {
            return config.content;
        }

        // Use render props pattern
        const ContentComponent = config.content.type;
        return <ContentComponent {...config.content.props} note={renderNoteElement()} />;
    };

    return (
        <Tooltip title={config.title} content={renderContentWithNote()} textAlign="center">
            <LensRowBadge className={config.badgeClass}>{config.badgeIcon}</LensRowBadge>
        </Tooltip>
    );
};
